import React, { useEffect, useState } from 'react'
import { Tab, Tabs, Box, Grid } from '@mui/material'
import { useLocation, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { useAppDispatch, useAppSelector } from 'redux/Hook'
import TextAnalysisFilters from '../Filters'
import TextAnalysisContent from '../Content'
import SummarisedResults from '../SummarisedResults'
import AIThemes from '../AIThemes'
import { loadTabAPIs } from '../Helpers/Helper'

enum TabValue {
  Contributions = 0,
  Themes = 1,
  Results = 2,
}

const TextAnalysisTabs: React.FC = () => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const location = useLocation()
  const navigate = useNavigate()

  const { search, pathname } = location
  const urlParams = new URLSearchParams(search)

  const { themes_keyphrasesToggle, platformThemes_keyphrases } = useAppSelector(
    (state: any) => state.textAnalysis.main.appConfig,
  )

  const isThemesTabVisible =
    themes_keyphrasesToggle && platformThemes_keyphrases

  // Define available tabs with their visibility
  const TABS = [
    {
      label: t('textAnalysis.tabs.contributions'),
      value: TabValue.Contributions,
      visible: true,
    },
    {
      label: t('textAnalysis.tabs.themes'),
      value: TabValue.Themes,
      visible: isThemesTabVisible,
    },
    {
      label: t('textAnalysis.tabs.results'),
      value: TabValue.Results,
      visible: true,
    },
  ].filter((tab) => tab.visible)

  // Tab index setup from URL or fallback
  const validTabValues = TABS.map((tab) => tab.value)
  const urlTabId = Number(urlParams.get('tab_id'))
  const defaultTab = validTabValues.includes(urlTabId)
    ? urlTabId
    : TabValue.Contributions

  const [tabIndex, setTabIndex] = useState(defaultTab)

  const handleTabChange = (
    _: React.SyntheticEvent | undefined,
    value: number,
  ) => {
    const newParams = new URLSearchParams(search)
    newParams.set('tab_id', value.toString())
    setTabIndex(value)
    navigate({ pathname, search: `?${newParams}` })
  }

  const switchToContributionsTab = () => {
    handleTabChange(undefined, TabValue.Contributions)
  }

  // Trigger tab-specific API when tab changes
  useEffect(() => {
    loadTabAPIs(dispatch, tabIndex)
  }, [tabIndex, dispatch])

  // If the current tab becomes invalid (e.g., Themes tab hidden via toggle), fallback to default tab
  useEffect(() => {
    if (!validTabValues.includes(tabIndex)) {
      handleTabChange(undefined, TabValue.Contributions)
    }
  }, [TABS])

  const renderTabContent = () => {
    switch (tabIndex) {
      case TabValue.Contributions:
        return (
          <>
            <TextAnalysisFilters />
            <TextAnalysisContent />
          </>
        )
      case TabValue.Themes:
        return isThemesTabVisible ? (
          <AIThemes switchToContributionsTab={switchToContributionsTab} />
        ) : null
      case TabValue.Results:
      default:
        return <SummarisedResults />
    }
  }

  return (
    <Box
      data-testid="ta-tabs-container"
      width="100%"
      height="calc(100% - 10rem)"
    >
      <Tabs
        value={
          validTabValues.includes(tabIndex) ? tabIndex : TabValue.Contributions
        }
        onChange={handleTabChange}
        sx={{ borderBottom: 1, borderColor: 'divider', height: '3rem' }}
        selectionFollowsFocus
        data-testid="ta-tabs-header"
        aria-label="Text Analysis Tabs"
        role="tablist"
      >
        {TABS.map((tab) => (
          <Tab
            key={tab.value}
            label={tab.label}
            value={tab.value}
            role="tab"
            aria-controls={`tabpanel-${tab.value}`}
            data-testid={`tab-${tab.label.toLowerCase()}`}
          />
        ))}
      </Tabs>

      <Grid
        container
        data-testid="ta-tabs-content"
        sx={{
          pt: '1.5rem',
          height: 'calc(100% - 3rem)',
        }}
      >
        {renderTabContent()}
      </Grid>
    </Box>
  )
}

export default TextAnalysisTabs
